export interface GuildQueryKey {
  guildId: string;
  resource: string;
  revision: number | string;
}

interface InFlightQuery<T> extends GuildQueryKey {
  controller: AbortController;
  epoch: number;
  promise: Promise<T>;
}

const API_BASE = "https://dashboard.raidlens.invalid";
const MAX_GUILD_ID_CHARACTERS = 64;
const MAX_RESOURCE_CHARACTERS = 1_024;
const MAX_REVISION_CHARACTERS = 128;

export class AuthenticatedGuildQueryCache {
  private epoch = 0;
  private principalId: string | null = null;
  private readonly inFlight = new Map<string, InFlightQuery<unknown>>();

  get sessionEpoch(): number {
    return this.epoch;
  }

  beginBootstrap(): number {
    this.advanceEpoch();
    return this.epoch;
  }

  adoptPrincipal(principalId: string): number {
    if (!principalId || principalId.length > MAX_GUILD_ID_CHARACTERS) {
      throw new TypeError("Principal identity is invalid");
    }
    if (this.principalId !== null && this.principalId !== principalId) {
      this.advanceEpoch();
    }
    this.principalId = principalId;
    return this.epoch;
  }

  clearSession(): number {
    this.principalId = null;
    this.advanceEpoch();
    return this.epoch;
  }

  handleAuthorizationFailure(status: 401 | 403): number {
    if (status === 401) this.principalId = null;
    this.advanceEpoch();
    return this.epoch;
  }

  query<T>(
    key: GuildQueryKey,
    loader: (signal: AbortSignal) => Promise<T>,
    signal?: AbortSignal,
  ): Promise<T> {
    const normalized = normalizeKey(key);
    const cacheKey = serializeKey(this.epoch, normalized);
    const existing = this.inFlight.get(cacheKey) as
      InFlightQuery<T> | undefined;
    if (existing) return observe(existing.promise, signal);

    const controller = new AbortController();
    const epoch = this.epoch;
    const promise = invokeLoader(loader, controller.signal).then((value) => {
      if (controller.signal.aborted || epoch !== this.epoch) {
        throw abortError();
      }
      return value;
    });
    const entry = {
      ...normalized,
      controller,
      epoch,
      promise,
    } satisfies InFlightQuery<T>;
    this.inFlight.set(cacheKey, entry);
    void entry.promise.then(
      () => {
        this.remove(cacheKey, entry);
      },
      () => {
        this.remove(cacheKey, entry);
      },
    );
    return observe(entry.promise, signal);
  }

  invalidateGuild(guildId: string): void {
    for (const [cacheKey, entry] of this.inFlight) {
      if (entry.guildId === guildId) this.invalidate(cacheKey, entry);
    }
  }

  invalidateResource(key: GuildQueryKey): void {
    const normalized = normalizeKey(key);
    for (const [cacheKey, entry] of this.inFlight) {
      if (
        entry.guildId === normalized.guildId &&
        entry.resource === normalized.resource &&
        entry.revision === normalized.revision
      ) {
        this.invalidate(cacheKey, entry);
      }
    }
  }

  private advanceEpoch(): void {
    for (const [cacheKey, entry] of this.inFlight) {
      this.invalidate(cacheKey, entry);
    }
    this.epoch = this.epoch === Number.MAX_SAFE_INTEGER ? 0 : this.epoch + 1;
  }

  private invalidate(cacheKey: string, entry: InFlightQuery<unknown>): void {
    entry.controller.abort();
    this.inFlight.delete(cacheKey);
  }

  private remove<T>(cacheKey: string, entry: InFlightQuery<T>): void {
    if (this.inFlight.get(cacheKey) === entry) {
      this.inFlight.delete(cacheKey);
    }
  }
}

function invokeLoader<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  signal: AbortSignal,
): Promise<T> {
  try {
    return loader(signal);
  } catch (error: unknown) {
    return Promise.reject(
      error instanceof Error
        ? error
        : new Error("Guild query failed", { cause: error }),
    );
  }
}

export const authenticatedGuildQueryCache = new AuthenticatedGuildQueryCache();

export function guildQueryKeyFromApiPath(path: string): GuildQueryKey | null {
  const url = sameOriginApiUrl(path);
  if (!url) return null;
  if (
    /^\/api\/v1\/admin\/assistant-(?:health|requests)(?:\/|$)/u.test(
      url.pathname,
    )
  ) {
    return normalizeKey({
      guildId: "@fleet",
      resource: `${url.pathname}${url.search}`,
      revision: "current",
    });
  }
  const match = /^\/api\/v1\/guilds\/([^/]+)(\/.*)$/u.exec(url.pathname);
  if (!match?.[1] || !match[2]) return null;
  const revision =
    url.searchParams.get("revision") ??
    url.searchParams.get("sourceRevision") ??
    url.searchParams.get("credentialRevision") ??
    url.searchParams.get("sourceCatalogRevision") ??
    "current";
  try {
    return normalizeKey({
      guildId: decodePathSegment(match[1]),
      resource: `${match[2]}${url.search}`,
      revision,
    });
  } catch {
    return null;
  }
}

export function guildIdFromMutationPath(path: string): string | null {
  const url = sameOriginApiUrl(path);
  if (!url) return null;
  const match =
    /^\/api\/v1\/guilds\/([^/]+)(?:\/|$)/u.exec(url.pathname) ??
    /^\/api\/v1\/admin\/guilds\/([^/]+)(?:\/|$)/u.exec(url.pathname);
  return match?.[1] ? decodePathSegment(match[1]) : null;
}

function normalizeKey(key: GuildQueryKey): GuildQueryKey {
  const guildId = key.guildId.trim();
  const resource = key.resource.trim();
  const revision =
    typeof key.revision === "string" ? key.revision.trim() : key.revision;
  if (!guildId || guildId.length > MAX_GUILD_ID_CHARACTERS) {
    throw new TypeError("Guild query identity is invalid");
  }
  if (!resource || resource.length > MAX_RESOURCE_CHARACTERS) {
    throw new TypeError("Guild query resource is invalid");
  }
  if (
    (typeof revision === "string" &&
      (!revision || revision.length > MAX_REVISION_CHARACTERS)) ||
    (typeof revision === "number" &&
      (!Number.isSafeInteger(revision) || revision < 0))
  ) {
    throw new TypeError("Guild query revision is invalid");
  }
  return { guildId, resource, revision };
}

function serializeKey(epoch: number, key: GuildQueryKey): string {
  return JSON.stringify([epoch, key.guildId, key.resource, key.revision]);
}

function observe<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(abortError());
  return new Promise<T>((resolve, reject) => {
    const abort = () => {
      reject(abortError());
    };
    signal.addEventListener("abort", abort, { once: true });
    void promise.then(
      (value) => {
        signal.removeEventListener("abort", abort);
        resolve(value);
      },
      (error: unknown) => {
        signal.removeEventListener("abort", abort);
        reject(
          error instanceof Error
            ? error
            : new Error("Guild query failed", { cause: error }),
        );
      },
    );
  });
}

function abortError(): Error {
  const error = new Error("Guild query was invalidated");
  error.name = "AbortError";
  return error;
}

function sameOriginApiUrl(path: string): URL | null {
  try {
    const url = new URL(path, API_BASE);
    return url.origin === API_BASE ? url : null;
  } catch {
    return null;
  }
}

function decodePathSegment(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}
